import React, { useState } from "react";
import { useMutation } from "react-query";
import { Platform, View } from "react-native";
import { instance, parseError } from "@clearabee/ui-sdk";
import {
  Panel,
  Text,
  theme,
  useResponsive,
} from "@clearabee/ui-library-native";
import { SkeletonLoader } from "components/SkeletonLoader/SkeletonLoader";
import { Rect } from "react-native-svg";
import { OrderTrain, StepName } from "components/OrderTrain/OrderTrain";
import { convertApiStatus } from "lib/utils";
import { JobViewProps } from "./types";
import { LargeScreenView } from "./LargeScreenView";
import { SmallScreenView } from "./SmallScreenView";
import { DownloadLink } from "./DownloadLink";
import { styles } from "./JobView.styles";

export const JobView = (props: JobViewProps): React.ReactElement => {
  const { screens } = useResponsive();
  const { job, isLoading, showTrain } = props;
  const [itemsCollapsed, collapseItems] = useState(true);
  const [error, setError] = useState("");
  const extendedProps = {
    itemsCollapsed,
    collapseItems,
    isCollapsable: (job?.items?.length ?? 0) > 3,
  };

  const isWasteTransferNoteReady =
    job?.collectionInformation?.wasteTypes &&
    job?.collectionInformation.cubicYardsCollected &&
    job?.collectionInformation.estimatedWeight;

  const isInvoiceReady = job?.status === "Completed";

  const downloadPdf = async (url: string, key: string) => {
    const blob = await fetch(url).then((r) => r.blob());
    const a = document.createElement("a");
    a.href = (window as any).URL.createObjectURL(blob);
    a.download = key;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const {
    mutate: getInvoice,
    isLoading: getInvoiceisLoading,
    isSuccess: getInvoiceisSuccess,
  } = useMutation(
    "getInvoice",
    async (id: string) => {
      return (await instance.jobs.getJobInvoicePdf(id)).data;
    },
    {
      onSuccess: async (data) => {
        downloadPdf(data.location, data.fileKey);
      },
      onError: (error) => {
        setError(parseError(error, "Something went wrong"));
      },
    },
  );

  const {
    mutate: getWasteTransferNote,
    isLoading: getWasteTransferNoteIsLoading,
    isSuccess: getWasteTransferNoteIsSuccess,
  } = useMutation(
    "getWasteTransferNote",
    async (id: string) => {
      return (await instance.jobs.getJobWasteTransferNotePdf(id)).data;
    },
    {
      onSuccess: async (data) => {
        downloadPdf(data.location, data.fileKey);
      },
      onError: (error) => {
        setError(parseError(error, "Something went wrong"));
      },
    },
  );

  return (
    <>
      {isLoading && (
        <Panel
          styles={{ padding: 0, marginBottom: theme.spacing.small }}
          shadow={Platform.OS === "web"}
        >
          <SkeletonLoader
            speed={1}
            backgroundColor={theme.colors.light.base}
            foregroundColor={`${theme.colors.greyscale.lighter}00`}
            height={108}
            width="100%"
          >
            <Rect x="0" y="0" rx="4" ry="4" width="100%" height="100%" />
          </SkeletonLoader>
        </Panel>
      )}
      {!isLoading && showTrain && (
        <Panel
          styles={{
            padding: theme.spacing.medium,
            marginBottom: theme.spacing.small,
          }}
          shadow={false}
        >
          <OrderTrain
            step={convertApiStatus(job?.status ?? "") as StepName}
            titleSize={screens.medium ? "xsmall" : "xsmall2"}
            iconSize={screens.medium ? "medium" : "small"}
            hideDescription
            minified
          />
        </Panel>
      )}

      {!isLoading && (isInvoiceReady || isWasteTransferNoteReady) && (
        <Panel styles={styles.resourcesPanel} shadow={false}>
          <Text styles={styles.documentsText}>Documents</Text>

          <View
            style={{
              ...styles.linksContainer,
              flexDirection: screens.medium ? "row" : "column",
            }}
          >
            {isInvoiceReady && (
              <DownloadLink
                isLoading={getInvoiceisLoading}
                isSuccess={getInvoiceisSuccess}
                linkText="Download Invoice"
                linkOnPress={() => {
                  setError("");
                  getInvoice(job.id ? String(job.id) : "");
                }}
              />
            )}

            {!!isWasteTransferNoteReady && (
              <DownloadLink
                isLoading={getWasteTransferNoteIsLoading}
                isSuccess={getWasteTransferNoteIsSuccess}
                linkText="Download Waste Transfer Note"
                linkOnPress={() => {
                  setError("");
                  getWasteTransferNote(job.id ? String(job.id) : "");
                }}
              />
            )}
          </View>

          {!!error && (
            <Text color="negative" fontSize="small">
              {error}
            </Text>
          )}
        </Panel>
      )}

      {Platform.OS === "web" && screens.medium ? (
        <LargeScreenView {...props} {...extendedProps} />
      ) : (
        <SmallScreenView {...props} {...extendedProps} />
      )}
    </>
  );
};
